import { Suspense } from 'react';

import { createRoot } from 'react-dom/client';

import { Amplify } from '@aws-amplify/core';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { NuqsAdapter } from 'nuqs/adapters/react';
import { ErrorBoundary } from 'react-error-boundary';

import { Toaster } from '@/components/ui/sonner';
import { ToastContainer } from '@/components/ui/toast-container';
import { queryClient } from '@/lib/query-client';
import { Themes } from '@/types/theme';

import { TooltipProvider } from './components/ui/tooltip';
import './core/i18n';
import './index.css';
import { AppRouter, ThemeProvider } from './modules/common';

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  sendDefaultPii: true,
});

Amplify.configure({
  Auth: {
    region: import.meta.env.VITE_AWS_REGION,
    userPoolId: import.meta.env.VITE_AWS_POOL_ID,
    userPoolWebClientId: import.meta.env.VITE_AWS_WEB_CLIENT_ID,
  },
});

createRoot(document.getElementById('root')!).render(
  <Suspense
    fallback={
      <div className="flex h-screen w-screen items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    }
  >
    <ThemeProvider defaultTheme={Themes.SYSTEM} storageKey="vite-ui-theme">
      <ErrorBoundary
        FallbackComponent={({ error }) => <div>Error: {error.message}</div>}
        key={location.pathname}
      >
        <QueryClientProvider client={queryClient}>
          <TooltipProvider delayDuration={100}>
            <NuqsAdapter>
              <AppRouter />
            </NuqsAdapter>
          </TooltipProvider>
        </QueryClientProvider>

        <ToastContainer theme={Themes.DARK} />
        <Toaster />
      </ErrorBoundary>
    </ThemeProvider>
  </Suspense>
);
